import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    CRM_DROPDOWN_MAX_OPTIONS,
    CRM_MAX_FIELD_LENGTH,
} from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Icon } from '../../../components/Icon';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { DropdownOption } from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { ActiveIngredients, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';

const temporaryActiveIngredients = [
  {
    ingredient: {
      name: '2,4D',
      value: '2,4D',
    },
    concentration: 0.8,
  },
  {
    ingredient: {
      name: 'Abamectin',
      value: 'abamectin',
    },
    concentration: 0.3,
  },
  {
    ingredient: {
      name: 'Acetamiprid',
      value: 'acetamiprid',
    },
    concentration: 0.4,
  },
];

// Use only ingredient values for dropdowns
const ingredientsOptions = (
  temporaryActiveIngredients: {
    ingredient: {
      name: string;
      value: string;
    };
    concentration: number;
  }[],
) =>
  temporaryActiveIngredients.map(
    ({ ingredient }) => ({ ...ingredient } as DropdownOption),
  );

export const StepActiveIngredient: FC<StepProps> = ({
  mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [{ activeIngredients }, { setActiveIngredients }] = useBriefingState();
  const [mutableActiveIngredients, setMutableActiveIngredients] =
    useState<ActiveIngredients | null>(null);
  const ingredientIndexExists = (index: number): boolean =>
    Boolean(activeIngredients && activeIngredients[index]);

  const isStepValid = useMemo(
    () =>
      (activeIngredients &&
        Object.keys(activeIngredients).length > 0 &&
        !Object.values(activeIngredients).some(
          ({ ingredient: value, concentration }) =>
            !(Boolean(value) && (concentration ?? 0) > 0),
        )) ||
      false,
    [activeIngredients],
  );

  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  useEffect(() => {
    setMutableActiveIngredients(
      activeIngredients ? { ...activeIngredients } : null,
    );
  }, [activeIngredients]);

  // Set fixed indexed state values for ingredient dropdown and concentration input
  // to correspond to the index of the active ingredient
  const [stateIngredient1, setStateIngredient1] =
    useState<DropdownOption | null>(
      activeIngredients && ingredientIndexExists(0)
        ? activeIngredients[0].ingredient
        : null,
    );
  const [stateConcentration1, setStateConcentration1] = useState<number | null>(
    activeIngredients && ingredientIndexExists(0)
      ? activeIngredients[0].concentration
      : null,
  );
  const [stateIngredient2, setStateIngredient2] =
    useState<DropdownOption | null>(
      activeIngredients && ingredientIndexExists(1)
        ? activeIngredients[1].ingredient
        : null,
    );
  const [stateConcentration2, setStateConcentration2] = useState<number | null>(
    activeIngredients && ingredientIndexExists(1)
      ? activeIngredients[1].concentration
      : null,
  );
  const [stateIngredient3, setStateIngredient3] =
    useState<DropdownOption | null>(
      activeIngredients && ingredientIndexExists(2)
        ? activeIngredients[2].ingredient
        : null,
    );
  const [stateConcentration3, setStateConcentration3] = useState<number | null>(
    activeIngredients && ingredientIndexExists(2)
      ? activeIngredients[2].concentration
      : null,
  );
  const [stateIngredient4, setStateIngredient4] =
    useState<DropdownOption | null>(
      activeIngredients && ingredientIndexExists(3)
        ? activeIngredients[3].ingredient
        : null,
    );
  const [stateConcentration4, setStateConcentration4] = useState<number | null>(
    activeIngredients && ingredientIndexExists(3)
      ? activeIngredients[3].concentration
      : null,
  );
  const [stateIngredient5, setStateIngredient5] =
    useState<DropdownOption | null>(
      activeIngredients && ingredientIndexExists(4)
        ? activeIngredients[4].ingredient
        : null,
    );
  const [stateConcentration5, setStateConcentration5] = useState<number | null>(
    activeIngredients && ingredientIndexExists(4)
      ? activeIngredients[4].concentration
      : null,
  );

  // Set an array to manage the amount of rows exisiting or added by user
  const [rowAmountArr, setRowAmountArr] = useState<number[]>(
    activeIngredients
      ? Array.from(Array(Object.keys(activeIngredients).length).keys())
      : [0],
  );
  const [clickedToAdd, setClickedToAdd] = useState(0);

  const onAddIngredient = () => {
    if (rowAmountArr.length < 5) {
      const lastElement = rowAmountArr.length - 1;
      const itemToAdd = rowAmountArr[lastElement] + 1;
      setRowAmountArr([...rowAmountArr, itemToAdd]);
      setClickedToAdd(clickedToAdd + 1);
    }
  };

  const onDeleteIngredient = useCallback(
    (index: number) => {
      setRowAmountArr(rowAmountArr.filter((item) => index !== item));
      if (
        mutableActiveIngredients &&
        Object.keys(mutableActiveIngredients).length
      ) {
        const newObj = mutableActiveIngredients;
        delete newObj[index];
        dispatch(setActiveIngredients(newObj));
      }
    },
    [rowAmountArr, dispatch, setActiveIngredients, mutableActiveIngredients],
  );

  const renderDropdownAndInputRow = useCallback(
    (
      index: number,
      stateValueAndFunction: {
        initialIngredient: DropdownOption | null;
        initialConcentration: number | null;
        setStateIngredient: React.Dispatch<
          React.SetStateAction<DropdownOption | null>
        >;
        setStateConcentration: React.Dispatch<
          React.SetStateAction<number | null>
        >;
      },
    ) => {
      const {
        initialIngredient,
        initialConcentration,
        setStateIngredient,
        setStateConcentration,
      } = stateValueAndFunction;

      return (
        <div className="flex justify-between" key={index}>
          <div className="w-full pr-3">
            <InputGroup
              label={t('topics:active-ingredients:chooseActiveIngredient')}
            >
              <Dropdown
                options={ingredientsOptions(temporaryActiveIngredients)}
                initialSelection={initialIngredient || undefined}
                typeahead={false}
                typeaheadMinChars={1}
                multiselect={false}
                maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                onChange={(option) => {
                  setStateIngredient(option as DropdownOption);
                  if (option) {
                    dispatch(
                      setActiveIngredients({
                        ...activeIngredients,
                        [index]: {
                          ingredient: option as DropdownOption,
                          concentration: initialConcentration,
                        },
                      }),
                    );
                  }
                }}
              />
            </InputGroup>
          </div>
          <div
            className="border-gray-700 w-9 border-t"
            style={{ marginTop: '55px' }}
          />
          <div className="w-full px-3">
            <InputGroup label={t('topics:active-ingredients:concentration')}>
              <div className="w-full">
                <Input
                  {...(initialConcentration
                    ? { initialValue: initialConcentration }
                    : {})}
                  type="number"
                  minNumber={0.1}
                  step={0.1}
                  onChange={(event) => {
                    const concentration = parseFloat(event.target.value);
                    setStateConcentration(concentration);
                    dispatch(
                      setActiveIngredients({
                        ...activeIngredients,
                        [index]: {
                          ingredient: initialIngredient,
                          concentration,
                        },
                      }),
                    );
                  }}
                  placeholder={'0.8'}
                  maxLength={CRM_MAX_FIELD_LENGTH}
                />
              </div>
            </InputGroup>
          </div>

          <div style={{ marginTop: '44px' }}>
            <button
              data-test="delete-ingredient-button"
              className="inline-flex items-center space-x-1 text-base font-regular cursor-pointer text-gray-600 focus:outline-none hover:text-blue-100 focus:text-blue-100"
              onClick={() => {
                onDeleteIngredient(index);
              }}
            >
              <div className="w-4 h-4">
                {index > 0 && <Icon name="Delete" />}
              </div>
            </button>
          </div>
        </div>
      );
    },
    [dispatch, setActiveIngredients, activeIngredients, onDeleteIngredient, t],
  );

  // Group state values and functions based off single index passed
  const getStateValuesAndFunctions = useCallback(
    (index: number) => {
      switch (index) {
        case 1:
          return {
            initialIngredient: stateIngredient2,
            initialConcentration: stateConcentration2,
            setStateIngredient: setStateIngredient2,
            setStateConcentration: setStateConcentration2,
          };

        case 2:
          return {
            initialIngredient: stateIngredient3,
            initialConcentration: stateConcentration3,
            setStateIngredient: setStateIngredient3,
            setStateConcentration: setStateConcentration3,
          };

        case 3:
          return {
            initialIngredient: stateIngredient4,
            initialConcentration: stateConcentration4,
            setStateIngredient: setStateIngredient4,
            setStateConcentration: setStateConcentration4,
          };

        case 4:
          return {
            initialIngredient: stateIngredient5,
            initialConcentration: stateConcentration5,
            setStateIngredient: setStateIngredient5,
            setStateConcentration: setStateConcentration5,
          };
        case 0:
        default:
          return {
            initialIngredient: stateIngredient1,
            initialConcentration: stateConcentration1,
            setStateIngredient: setStateIngredient1,
            setStateConcentration: setStateConcentration1,
          };
      }
    },
    [
      stateConcentration1,
      stateConcentration2,
      stateConcentration3,
      stateConcentration4,
      stateConcentration5,
      stateIngredient1,
      stateIngredient2,
      stateIngredient3,
      stateIngredient4,
      stateIngredient5,
    ],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        text={t('topics:active-ingredients:heading:general')}
        margin={0}
        optionalField={mandatoryFields['activeIngredients']?.length === 0}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <div className="w-full md:w-9/12">
          <TemplateFlexGridContainer.BriefingStepSection>
            {rowAmountArr.map((rowNo) =>
              renderDropdownAndInputRow(
                rowNo,
                getStateValuesAndFunctions(rowNo),
              ),
            )}

            {rowAmountArr.length < 5 && (
              <button
                data-test="add-ingredient-button"
                className="inline-flex items-center space-x-1 text-base font-regular text-blue-200 cursor-pointer focus:outline-none hover:text-blue-100 focus:text-blue-100"
                onClick={() => {
                  onAddIngredient();
                }}
              >
                <div className="w-3 h-3">
                  <Icon name="Add" />
                </div>
                <p>{t('topics:active-ingredients:addAnother')}</p>
              </button>
            )}
          </TemplateFlexGridContainer.BriefingStepSection>
        </div>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
